import { AdditiveBlending, BufferAttribute, BufferGeometry, Points, ShaderMaterial } from 'three';
import { GPUComputationRenderer } from 'three/addons';

import { gsap } from '@resn/gsap';

import glslCurlNoise from '@/libs/lygia/generative/curl.glsl';
import { logger } from '~/utils/logger';

export class NebulaEffect {
    constructor(parent, renderer) {
        this.parent = parent;
        this.renderer = renderer;

        this.time = 0;
        this.tLastFrame = null;
        this.active = false;

        this.pixelRatio = this.renderer ? this.renderer.getPixelRatio() : 1.0;

        this.params = {
            timeScale: 0.2,
            positionScale: 20,
            speedScale: 30.0,

            particleCount: 512,
            basePointSize: 1,

            colorHueBase: 0.1,
            colorHueRange: 0.0,
            colorSaturation: 0.4,
            colorLightness: 0.5,

            opacityBase: 0.3,
            opacityRange: 0.3,

            gravityStrength: 30.0,
            gravitySphereRadius: 8.0,
            gravitySphereNoiseScale: 0.01, // Scale for the noise that affects sphere radius
            gravitySphereNoiseStrength: 0.0, // How much the noise affects the radius
            gravitySphereNoiseSpeed: 0.2, // Speed of radius variation

            explosionStrength: 140.0,
            explosionRandomness: 0.0,
            explosionNoiseScale: 0.15,
            explosionDuration: 0.1,
            explosionTime: 0.0,

            explosionRotationStrength: 300.0, // Strength of rotational force
            explosionRotationScale: 0.05, // Scale of rotation noise pattern
            curlNoiseBaseScale: 0.1, // Base scale for curl noise
            curlNoiseScaleRange: 0.1, // How much to increase scale when near center

            phaseNoiseStrength: 0.1,
            phaseNoiseSpeed: 0.1,
        };

        this.particleScale = 0.03;
        this.hideTween = null;
    }

    async init(renderOrder = 2) {
        this.setupGPGPU();
        this.createParticles(renderOrder);

        this.updateUniforms();

        return this;
    }

    async setupGPGPU() {
        // Initialize GPGPU
        this.gpuCompute = new GPUComputationRenderer(
            this.params.particleCount,
            this.params.particleCount,
            this.renderer
        );

        const glslPosition = /* glsl */ `
            uniform float time;
            uniform float delta;
            uniform float resetSimulation;

            void main() {
                vec2 uv = gl_FragCoord.xy / resolution.xy;
                
                // Get current position and velocity
                vec4 tmpPos = texture2D(texturePosition, uv);
                vec3 position = tmpPos.xyz;

                vec4 tmpVel = texture2D(textureVelocity, uv);
                vec3 velocity = tmpVel.xyz;

                // If reset flag is set, return to initial position
                if (resetSimulation > 0.5) {
                    // Calculate initial position (same as in fillPositionTexture)
                    float i = (uv.y * resolution.y * resolution.x + uv.x * resolution.x);
                    float phi = acos(1.0 - 2.0 * (i / (resolution.x * resolution.y)));
                    float theta = 3.14159 * (1.0 + sqrt(5.0)) * i;
                    
                    float x = cos(theta) * sin(phi);
                    float y = sin(theta) * sin(phi);
                    float z = cos(phi) * 0.5;
                    
                    gl_FragColor = vec4(x, y, z, 1.0);
                    return;
                }

                // Normal update code
                // Update position based on velocity
                position += velocity * delta;

                gl_FragColor = vec4(position, 1.0);
            }
        `;

        const glslVelocity = /* glsl */ `
        uniform float time;
            uniform float delta;
            uniform float timeScale;
            uniform float speedScale;
            uniform float gravityStrength;
            uniform float gravitySphereRadius;
            uniform float gravitySphereNoiseScale;
            uniform float gravitySphereNoiseStrength;
            uniform float gravitySphereNoiseSpeed;
            uniform float explosionStrength;
            uniform float explosionRandomness;
            uniform float explosionNoiseScale;
            uniform float explosionDuration;
            uniform float explosionTime;
            uniform float explosionRotationStrength;
            uniform float explosionRotationScale;
            uniform float curlNoiseBaseScale;
            uniform float curlNoiseScaleRange;
            uniform float phaseNoiseStrength;
            uniform float phaseNoiseSpeed;
            
            ${glslCurlNoise}

            // Function to get distance-based noise scale
            float getNoiseScale(vec3 pos, float phase) {
                float dist = length(pos);
                float maxDist = gravitySphereRadius;
                float normalizedDist = dist / maxDist;
                
                // Create unique phase-based modulation with strength control
                float phaseMod = sin(phase * 5.0 + time * phaseNoiseSpeed) * 0.5 + 0.5;
                float phaseScale = 1.0 + phaseMod * phaseNoiseStrength;
                
                // Add random seed based on phase
                float phaseSeed = fract(phase * 123.456);
                
                // Vary scale with phase-based pattern, controlled by strength
                float scale = curlNoiseBaseScale + 
                            (curlNoiseScaleRange * (1.0 - normalizedDist)) * 
                            (1.0 + phaseNoiseStrength * sin(phaseSeed * 62.83185)); // 62.83185 ≈ 10*2π
                
                return scale * phaseScale;
            }

            // Function to get dynamic gravity sphere radius
            float getDynamicRadius(vec3 pos, float phase) {
                // Create a position for noise sampling that rotates over time
                vec3 noisePos = normalize(pos) * gravitySphereNoiseScale;
                
                // Use curl noise magnitude to vary the radius
                vec3 radiusNoise = curl(noisePos+ vec3(time * timeScale));
                float noiseMagnitude = length(radiusNoise);
                
                // Vary radius based on noise
                float dynamicRadius = gravitySphereRadius * (1.0 + (noiseMagnitude - 1.0) * gravitySphereNoiseStrength);
                return dynamicRadius;
            }

            // Function to get explosion rotation force
            vec3 getExplosionRotation(vec3 pos, float progress) {
                // Create rotation axis based on curl noise
                vec3 rotationNoisePos = pos * explosionRotationScale;
                vec3 rotationAxis = curl(rotationNoisePos + vec3(time * 0.1));
                
                // Cross product with position creates perpendicular force (rotation)
                vec3 rotationForce = cross(normalize(rotationAxis), normalize(pos));
                
                // Scale force by distance from center for more dramatic outer rotation
                float distanceScale = length(pos) / gravitySphereRadius;
                
                return rotationForce * explosionRotationStrength * distanceScale;
            }

            void main() {
                vec2 uv = gl_FragCoord.xy / resolution.xy;
                vec4 tmpPos = texture2D(texturePosition, uv);
                vec4 tmpVel = texture2D(textureVelocity, uv);
                vec3 position = tmpPos.xyz;
                vec3 velocity = tmpVel.xyz;
                float phase = tmpVel.w;

                // Get dynamic radius for current position
                float dynamicRadius = getDynamicRadius(position, phase);
                
                // Get noise scale based on distance from center
                float dynamicNoiseScale = getNoiseScale(position, phase);
                
                // Apply curl noise with dynamic scale
                vec3 noisePos = position * dynamicNoiseScale;
                // vec3 curl = curl(noisePos + vec3(time * timeScale));
                vec3 outCurl = curl(position * dynamicNoiseScale + vec3(time * timeScale));

                // Update velocity using curl noise
                velocity += outCurl * speedScale * delta;

                float explosionTimeOffset = snoise(position) * 0.5;
                float explosionTimeCorrected = max(0.0, explosionTime - explosionTimeOffset);

                // Apply gravity towards center when outside the dynamic sphere radius
                float dist = length(position);
                // if (dist > dynamicRadius) {
                    vec3 gravityDir = normalize(-position);
                    float gravityFalloff = (dynamicRadius - dist);
                    velocity -= gravityDir * gravityStrength * gravityFalloff * delta * .02 * min(1.0, explosionTimeCorrected / explosionDuration);
                // }

                // Add explosive force if in explosion state

                if (explosionTimeCorrected < explosionDuration) {
                    // Use curl noise for directional randomness
                    vec3 explosionNoisePos = position * explosionNoiseScale;
                    vec3 curlRandom = curl(explosionNoisePos + vec3(time * 0.1));
                    
                    // Mix between normal explosion direction and curl noise
                    vec3 baseExplosionDir = normalize(position);
                    vec3 finalExplosionDir = normalize(mix(baseExplosionDir, curlRandom, explosionRandomness));
                    
                    // Get rotational force
                    vec3 rotationForce = getExplosionRotation(position, explosionTimeCorrected/explosionDuration);
                    
                    // Combine outward and rotational forces
                    float explosionProgress = 1.0 - explosionTimeCorrected/explosionDuration;
                    vec3 outwardForce = finalExplosionDir * explosionStrength * explosionProgress;
                    vec3 explosionForce = outwardForce + rotationForce * explosionProgress;
                    
                    velocity += explosionForce * delta;
                }

                // Add damping
                velocity *= 0.96;

                // Update phase based on velocity
                phase += length(velocity) * delta;
                if (phase > 6.28318530718) phase -= 6.28318530718;

                gl_FragColor = vec4(velocity, phase);
            }
        `;

        // Create textures
        const texPosition = (this.texPosition = this.gpuCompute.createTexture());
        const texVelocity = (this.texVelocity = this.gpuCompute.createTexture());

        this.fillPositionTexture(texPosition);
        this.fillVelocityTexture(texVelocity);

        // Add variables to GPGPU
        this.positionVariable = this.gpuCompute.addVariable(
            'texturePosition',
            glslPosition,
            texPosition
        );
        this.velocityVariable = this.gpuCompute.addVariable(
            'textureVelocity',
            glslVelocity,
            texVelocity
        );

        // Add variable dependencies
        this.gpuCompute.setVariableDependencies(this.positionVariable, [
            this.positionVariable,
            this.velocityVariable,
        ]);
        this.gpuCompute.setVariableDependencies(this.velocityVariable, [
            this.positionVariable,
            this.velocityVariable,
        ]);

        // Add uniform variables
        const positionUniforms = this.positionVariable.material.uniforms;
        const velocityUniforms = this.velocityVariable.material.uniforms;

        Object.assign(positionUniforms, {
            time: { value: 0.0 },
            delta: { value: 0.0 },
            resetSimulation: { value: 0.0 },
        });

        Object.assign(velocityUniforms, {
            // Basic simulation parameters
            time: { value: 0.0 },
            delta: { value: 0.0 },
            timeScale: { value: this.params.timeScale },
            speedScale: { value: this.params.speedScale },

            // Gravity parameters
            gravityStrength: { value: this.params.gravityStrength },
            gravitySphereRadius: { value: this.params.gravitySphereRadius },
            gravitySphereNoiseScale: { value: this.params.gravitySphereNoiseScale },
            gravitySphereNoiseStrength: { value: this.params.gravitySphereNoiseStrength },
            gravitySphereNoiseSpeed: { value: this.params.gravitySphereNoiseSpeed },

            // Explosion parameters
            explosionStrength: { value: this.params.explosionStrength },
            explosionRandomness: { value: this.params.explosionRandomness },
            explosionNoiseScale: { value: this.params.explosionNoiseScale },
            explosionDuration: { value: this.params.explosionDuration },
            explosionTime: { value: 0.0 },
            explosionRotationStrength: { value: this.params.explosionRotationStrength },
            explosionRotationScale: { value: this.params.explosionRotationScale },

            // Noise and curl parameters
            curlNoiseBaseScale: { value: this.params.curlNoiseBaseScale },
            curlNoiseScaleRange: { value: this.params.curlNoiseScaleRange },
            phaseNoiseStrength: { value: this.params.phaseNoiseStrength },
            phaseNoiseSpeed: { value: this.params.phaseNoiseSpeed },
        });

        // Check for errors
        const error = this.gpuCompute.init();
        if (error !== null) {
            logger.error('NebulaEffect GPU computation initialization failed', error);
        }
    }

    fillPositionTexture(texture) {
        const arr = texture.image.data;

        for (let k = 0, kl = arr.length; k < kl; k += 4) {
            // Generate points on a sphere using fibonacci spiral
            const i = k / 4;
            const phi = Math.acos(1 - 2 * (i / (arr.length / 4)));
            const theta = Math.PI * (1 + Math.sqrt(5)) * i;

            // Convert to Cartesian coordinates on a sphere
            let x = Math.cos(theta) * Math.sin(phi);
            let y = Math.sin(theta) * Math.sin(phi);
            let z = Math.cos(phi) * 0.5;

            arr[k + 0] = x;
            arr[k + 1] = y;
            arr[k + 2] = z;
            arr[k + 3] = Math.random(); // Phase
        }
    }

    fillVelocityTexture(texture) {
        const arr = texture.image.data;
        for (let k = 0, kl = arr.length; k < kl; k += 4) {
            arr[k + 3] = Math.random() * Math.PI * 2;
        }
    }

    updateUniforms() {
        for (const key in this.params) {
            if (this.particles.material.uniforms[key])
                this.particles.material.uniforms[key].value = this.params[key];
            if (this.positionVariable.material.uniforms[key])
                this.positionVariable.material.uniforms[key].value = this.params[key];
            if (this.velocityVariable.material.uniforms[key])
                this.velocityVariable.material.uniforms[key].value = this.params[key];
        }

        this.resetGPUCompute();
    }

    createParticles(renderOrder = 2) {
        logger.debug('NebulaEffect: creating particles');
        try {
            const geometry = new BufferGeometry();
            const positions = new Float32Array(
                this.params.particleCount * this.params.particleCount * 3
            );
            const reference = new Float32Array(
                this.params.particleCount * this.params.particleCount * 2
            );

            for (let i = 0; i < this.params.particleCount * this.params.particleCount; i++) {
                const x = (i % this.params.particleCount) / this.params.particleCount;
                const y = ~~(i / this.params.particleCount) / this.params.particleCount;
                reference[i * 2] = x;
                reference[i * 2 + 1] = y;
            }

            geometry.setAttribute('position', new BufferAttribute(positions, 3));
            geometry.setAttribute('reference', new BufferAttribute(reference, 2));

            // Create material
            const material = new ShaderMaterial({
                uniforms: {
                    texturePosition: { value: null },
                    textureVelocity: { value: null },

                    colorHueBase: { value: this.params.colorHueBase },
                    colorHueRange: { value: this.params.colorHueRange },
                    colorSaturation: { value: this.params.colorSaturation },
                    colorLightness: { value: this.params.colorLightness },

                    opacityBase: { value: this.params.opacityBase },
                    opacityRange: { value: this.params.opacityRange },

                    basePointSize: { value: this.params.basePointSize },
                    pixelRatio: { value: this.pixelRatio },

                    time: { value: 0.0 },
                    collapseBlend: { value: 0.0 },
                },
                vertexShader: /* glsl */ `
                    uniform sampler2D texturePosition;
                    uniform sampler2D textureVelocity;
                    uniform float basePointSize;
                    uniform float pixelRatio;
                    uniform float collapseBlend;
                    
                    attribute vec2 reference;
                    
                    varying vec3 vVelocity;

                    attribute vec3 color;
                    attribute float size;
                    varying vec3 vColor;
                    varying float vPhase;
                    varying float vSpeed;

                    void main() {
                        vec4 posTemp = texture2D(texturePosition, reference);
                        vec4 velTemp = texture2D(textureVelocity, reference);
                        
                        vec3 pos = posTemp.xyz;
                        vVelocity = velTemp.xyz;

                        // Visual-only collapse — sim keeps running, no velocity/color shift
                        float collapse = collapseBlend * collapseBlend * (3.0 - 2.0 * collapseBlend);
                        pos *= 1.0 - collapse;
                        
                        vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
                        gl_Position = projectionMatrix * mvPosition;

                        vec4 velData = texture2D(textureVelocity, reference);
                        vPhase =  velData.w; // Particle's unique phase
                        vSpeed = length(velData.xyz); // Speed magnitude
                        
                        gl_PointSize = basePointSize * pixelRatio * (1.0 - collapse * 0.6);

                        vColor = color; 
                    }
                `,
                fragmentShader: /* glsl */ `
                    uniform float colorHueBase;
                    uniform float colorHueRange;
                    uniform float colorSaturation;
                    uniform float colorLightness;
                    uniform float opacityBase;
                    uniform float opacityRange;
                    
                    varying vec3 vVelocity;
                    uniform float time; 
                    varying vec3 vColor;
                    varying float vPhase;
                    varying float vSpeed;

                    float hue2rgb(float f1, float f2, float hue) {
                        if (hue < 0.0)
                            hue += 1.0;
                        else if (hue > 1.0)
                            hue -= 1.0;
                        float res;
                        if ((6.0 * hue) < 1.0)
                            res = f1 + (f2 - f1) * 6.0 * hue;
                        else if ((2.0 * hue) < 1.0)
                            res = f2;
                        else if ((3.0 * hue) < 2.0)
                            res = f1 + (f2 - f1) * ((2.0 / 3.0) - hue) * 6.0;
                        else
                            res = f1;
                        return res;
                    }

                    vec3 hsl2rgb(float h, float s, float l) {
                        vec3 rgb;
                        
                        if (s == 0.0) {
                            rgb = vec3(l);
                        } else {
                            float f2;
                            
                            if (l < 0.5)
                                f2 = l * (1.0 + s);
                            else
                                f2 = l + s - l * s;
                                
                            float f1 = 2.0 * l - f2;
                            
                            rgb.r = hue2rgb(f1, f2, h + 1.0/3.0);
                            rgb.g = hue2rgb(f1, f2, h);
                            rgb.b = hue2rgb(f1, f2, h - 1.0/3.0);
                        }
                        return rgb;
                    }
                    
                    void main() {
                        float speed = length(vVelocity);
                        float hue = colorHueBase + speed * .5 * colorHueRange - colorHueRange * .25;
    
                        vec3 color = hsl2rgb(hue, colorSaturation, colorLightness);
                        float gamma  = max(0.8, min(2.0, 1.0 / speed * 10.0));
                        color = pow(color, vec3(gamma));
                        float opacity = opacityBase;//min(1.0, opacityBase + min(1.0, speed) * opacityRange);
                        
                        float glitter = sin(vPhase * 1.0+time*0.1) * 0.5 + 0.5;
                        glitter = pow(glitter, 4.0);
                        
                        // Combine with base color
                        vec3 finalColor = color * (opacity * (glitter));// + vec3(glitter * 0.5);
                        // float finalOpacity = opacity * (glitter);
                        
                        gl_FragColor = vec4(finalColor, 1.);
                    }
                `,
                depthWrite: false,
                depthTest: false,
                transparent: false,
                blending: AdditiveBlending,
                name: 'NebulaMaterial',
            });

            this.particles = new Points(geometry, material);
            this.particles.frustumCulled = false; // Prevent particles from being culled when outside the camera's view
            this.particles.scale.setScalar(this.particleScale);
            this.particles.renderOrder = renderOrder;
            this.particles.visible = false; // Particles are hidden initially

            this.parent.add(this.particles);
            logger.debug('NebulaEffect: particles created successfully');
        } catch (e) {
            logger.error('Particle creation failed', e);
        }
    }

    show() {
        this.hideTween?.kill();
        this.hideTween = null;

        // Make particles visible immediately
        this.active = true;
        this.particles.visible = true;
    }

    hide({ duration = 0.5, delay = 0, ease = 'power2.in', collapse = false } = {}) {
        this.hideTween?.kill();

        if (collapse) {
            this.hideTween = this.collapseHide({ duration, delay, ease });
            return this.hideTween;
        }

        const { uniforms } = this.particles.material;
        this.hideTween = gsap.to(uniforms.opacityBase, {
            value: 0,
            duration,
            delay,
            ease,
            onComplete: () => this.reset(),
        });

        return this.hideTween;
    }

    collapseHide({ duration = 0.8, delay = 0, ease = 'power2.inOut' } = {}) {
        const uniforms = this.particles.material.uniforms;
        uniforms.collapseBlend.value = 0;

        return gsap
            .timeline({
                delay,
                onComplete: () => this.reset(),
            })
            .to(uniforms.collapseBlend, { value: 1, duration, ease }, 0)
            .to(
                uniforms.opacityBase,
                {
                    value: 0,
                    duration: duration * 0.25,
                    ease: 'power2.in',
                },
                duration * 0.72
            );
    }

    reset() {
        this.hideTween?.kill();
        this.hideTween = null;

        this.active = false;
        this.particles.visible = false;

        // Reset runtime state
        this.time = 0;
        this.timeAcc = 0;
        this.tLastFrame = null;

        this.particles.scale.setScalar(this.particleScale);

        // Reset GPU compute uniforms
        if (this.velocityVariable?.material.uniforms) {
            this.velocityVariable.material.uniforms['explosionTime'].value = 0.0;
            this.velocityVariable.material.uniforms['explosionStrength'].value =
                this.params.explosionStrength;
            this.velocityVariable.material.uniforms['gravityStrength'].value =
                this.params.gravityStrength;

            // Update all time-related uniforms
            this.velocityVariable.material.uniforms['time'].value = 0;
            this.velocityVariable.material.uniforms['delta'].value = 0;
        }

        if (this.positionVariable?.material.uniforms) {
            this.positionVariable.material.uniforms['time'].value = 0;
            this.positionVariable.material.uniforms['delta'].value = 0;
        }

        this.particles.material.uniforms.time.value = 0;
        this.particles.material.uniforms.opacityBase.value = this.params.opacityBase;
        this.particles.material.uniforms.collapseBlend.value = 0;

        // Update pixel ratio to current value
        this.updatePixelRatio();
        this.resetGPUCompute();
    }

    resetGPUCompute() {
        this.positionVariable.material.uniforms.resetSimulation.value = 1.0;
        this.gpuCompute.compute();
        this.positionVariable.material.uniforms.resetSimulation.value = 0.0;
    }

    triggerExplosion(power = 200) {
        this.velocityVariable.material.uniforms['explosionTime'].value = 0;
        this.velocityVariable.material.uniforms['explosionStrength'].value = power * 4;
    }

    animate({ deltaTime = 1 / 60 } = {}) {
        if (!this.particles || !this.active) return;

        // Cap deltaTime to prevent huge jumps on slow frames
        const cappedDelta = Math.min(deltaTime, 1 / 10);

        // Add actual time to overall simulation time
        this.time += cappedDelta;

        // Accumulate time for fixed-step updates
        this.timeAcc = (this.timeAcc || 0) + cappedDelta;

        // Fixed simulation timestep - 60fps
        const fixedTimestep = 1 / 60;

        // Only perform simulation when we've accumulated enough time
        if (this.timeAcc >= fixedTimestep) {
            // Update GPGPU simulation with fixed timestep
            this.positionVariable.material.uniforms['time'].value = this.time;
            this.positionVariable.material.uniforms['delta'].value = fixedTimestep;

            this.velocityVariable.material.uniforms['time'].value = this.time;
            this.velocityVariable.material.uniforms['delta'].value = fixedTimestep;

            this.velocityVariable.material.uniforms['explosionTime'].value += fixedTimestep;
            this.particles.material.uniforms.time.value = this.time;

            this.gpuCompute.compute();

            // Reset accumulator, but keep any remainder for next frame
            this.timeAcc %= fixedTimestep;
        }

        // Update particle uniforms with the latest GPU compute results
        const fboPos = this.gpuCompute.getCurrentRenderTarget(this.positionVariable);
        const fboVel = this.gpuCompute.getCurrentRenderTarget(this.velocityVariable);

        // Update particle uniforms (do this once per frame)
        this.particles.material.uniforms['texturePosition'].value = fboPos.texture;
        this.particles.material.uniforms['textureVelocity'].value = fboVel.texture;
    }

    dispose() {
        if (this.particles) {
            this.hide({ duration: 0 }); // Immediate hide

            this.parent.remove(this.particles);
            this.particles.geometry.dispose();
            this.particles.material.dispose();
        }

        this.gpuCompute.dispose();
    }

    updatePixelRatio() {
        if (!this.renderer) return;

        // Get current pixel ratio from renderer
        const currentPixelRatio = this.renderer.getPixelRatio();

        // Only update if changed
        if (this.pixelRatio !== currentPixelRatio) {
            this.pixelRatio = currentPixelRatio;

            // Update shader uniform if material exists
            if (this.particles?.material.uniforms.pixelRatio) {
                this.particles.material.uniforms.pixelRatio.value = this.pixelRatio;
            }
        }
    }
}
